"use client";
import React, { useState, useEffect, useRef } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider, { Settings } from "react-slick";
import Image from "next/image";
import {
  Star,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";

interface ReviewFormData {
  name: string;
  email: string;
  message: string;
  rating: number;
  expedition: string;
  profileImage: string;
}

interface Review {
  name: string;
  email?: string;
  message: string;
  rating: number;
  expedition?: string;
  images?: string[];
  isVerified?: boolean;
  _id?: string;
  createdAt?: string;
}

interface PackageReviewProps {
  expeditionId: string;
  expeditionName?: string;
}

const RATING_LABELS: { [key: number]: string } = {
  1: "Needs Improvement",
  2: "Fair Experience",
  3: "Good Journey",
  4: "Very Good Expedition",
  5: "Exceptional / Summit Success",
};

const PackageReview: React.FC<PackageReviewProps> = ({
  expeditionId,
  expeditionName = "This Expedition",
}) => {
  const [rating, setRating] = useState<number>(5);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [formData, setFormData] = useState<ReviewFormData>({
    name: "",
    email: "",
    message: "",
    rating: 5,
    expedition: expeditionId,
    profileImage: "",
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [reviewsData, setReviewsData] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [profilePreview, setProfilePreview] = useState<string>("");
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  const sliderRef = useRef<Slider>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Fetch existing reviews for this expedition
  const fetchReviews = async () => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/review/by-expiditionId/${expeditionId}`
      );
      if (response.ok) {
        const data = await response.json();
        setReviewsData(data.data || []);
      }
    } catch (error) {
      // Quiet fail on network hiccups
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expeditionId) {
      fetchReviews();
    }
  }, [expeditionId]);

  // Keep expedition id in sync
  useEffect(() => {
    setFormData((prev) => ({ ...prev, expedition: expeditionId }));
  }, [expeditionId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRatingClick = (selectedRating: number) => {
    setRating(selectedRating);
    setFormData((prev) => ({
      ...prev,
      rating: selectedRating,
    }));
  };

  const handleProfileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB limit");
      return;
    }

    setIsUploading(true);
    setProfilePreview(URL.createObjectURL(file));

    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("upload_preset", "website");

      const response = await fetch(
        "https://api.cloudinary.com/v1_1/dbbl19osz/image/upload",
        {
          method: "POST",
          body: uploadData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData((prev) => ({
          ...prev,
          profileImage: data.secure_url,
        }));
        toast.success("Profile photo uploaded!");
      } else {
        toast.error("Failed to upload photo. Please try again.");
        setProfilePreview("");
      }
    } catch (error) {
      toast.error("Error uploading profile photo");
      setProfilePreview("");
    } finally {
      setIsUploading(false);
    }
  };

  const removeProfileImage = () => {
    setProfilePreview("");
    setFormData((prev) => ({
      ...prev,
      profileImage: "",
    }));
  };

  const scrollToForm = () => {
    setIsFormOpen(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.rating) {
      toast.error("Please fill in your name, email and provide a rating.");
      return;
    }

    if (!formData.message.trim()) {
      toast.error("Please write a few words about your experience.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            expedition: expeditionId,
          }),
        }
      );

      if (response.ok) {
        toast.success("Thank you! Your review was submitted successfully.");
        // Reset form
        setFormData({
          name: "",
          email: "",
          message: "",
          rating: 5,
          expedition: expeditionId,
          profileImage: "",
        });
        setRating(5);
        setProfilePreview("");
        setIsFormOpen(false);

        // Refresh reviews list
        await fetchReviews();
      } else {
        toast.error("Failed to submit review. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter reviews (treat isVerified !== false as visible to support automatic or pending flags)
  const verifiedReviews = reviewsData.filter(
    (review) => review.isVerified !== false
  );

  const totalReviews = verifiedReviews.length;
  const averageRating =
    totalReviews > 0
      ? (
          verifiedReviews.reduce(
            (acc, r) => acc + (Number(r.rating) || 5),
            0
          ) / totalReviews
        ).toFixed(1)
      : "5.0";

  const sliderSettings: Settings = {
    dots: true,
    infinite: verifiedReviews.length > 2,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: false,
    autoplay: verifiedReviews.length > 2,
    autoplaySpeed: 5000,
    responsive: [
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <div className="w-full space-y-10">
      {/* 1. Header Subtitle & Action */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-zinc-200">
          <p className="text-zinc-600 text-sm sm:text-base max-w-xl">
            Authentic feedback and summit stories from adventurers who
            experienced {expeditionName} with our certified Sherpa team.
          </p>

          {/* Action Button */}
          <button
            onClick={scrollToForm}
            type="button"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-[#E25C23] hover:bg-[#c94b17] text-white font-semibold text-sm rounded-lg shadow-sm hover:shadow transition-all duration-200 shrink-0 cursor-pointer"
          >
            Write a Review
          </button>
        </div>

        {/* Aggregate Stats Card */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-zinc-50 border border-zinc-200/80 rounded-xl p-4 sm:p-5">
          {/* Average Rating */}
          <div className="flex items-center gap-4 border-b sm:border-b-0 sm:border-r border-zinc-200/80 pb-4 sm:pb-0 sm:pr-4">
            <div className="w-12 h-12 rounded-xl bg-[#E25C23] text-white flex flex-col items-center justify-center font-extrabold text-xl shadow-xs">
              {averageRating}
            </div>
            <div>
              <div className="flex items-center gap-1 text-[#E25C23]">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-4 h-4 ${
                      star <= Math.round(Number(averageRating))
                        ? "fill-[#E25C23] text-[#E25C23]"
                        : "text-zinc-300"
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mt-1">
                {totalReviews > 0
                  ? `Based on ${totalReviews} review${totalReviews > 1 ? "s" : ""}`
                  : "Climber Rating"}
              </p>
            </div>
          </div>

          {/* Summit & Safety Standards */}
          <div className="flex flex-col justify-center border-b sm:border-b-0 sm:border-r border-zinc-200/80 pb-4 sm:pb-0 sm:pr-4">
            <p className="text-sm font-bold text-zinc-900">
              100% Verified Trips
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Direct feedback from registered mountaineers
            </p>
          </div>

          {/* Guide Excellence */}
          <div className="flex flex-col justify-center">
            <p className="text-sm font-bold text-zinc-900">
              IFMGA / NNMGA Guides
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              High-altitude safety & Sherpa expertise
            </p>
          </div>
        </div>
      </div>

      {/* 2. Reviews Display Section */}
      <div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-8">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-white border border-zinc-200 rounded-2xl p-6 animate-pulse space-y-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-zinc-200" />
                  <div className="space-y-2 flex-1">
                    <div className="h-4 bg-zinc-200 rounded w-1/3" />
                    <div className="h-3 bg-zinc-200 rounded w-1/4" />
                  </div>
                </div>
                <div className="h-3 bg-zinc-200 rounded w-full" />
                <div className="h-3 bg-zinc-200 rounded w-5/6" />
                <div className="h-3 bg-zinc-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : verifiedReviews.length > 0 ? (
          <div>
            {/* Carousel Controls if more than 2 reviews */}
            {verifiedReviews.length > 2 && (
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Showing {verifiedReviews.length} climber reviews
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => sliderRef.current?.slickPrev()}
                    aria-label="Previous review"
                    type="button"
                    className="w-9 h-9 rounded-full border border-zinc-200 bg-white hover:bg-[#E25C23] hover:border-[#E25C23] hover:text-white text-zinc-700 flex items-center justify-center transition-all duration-200 shadow-sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => sliderRef.current?.slickNext()}
                    aria-label="Next review"
                    type="button"
                    className="w-9 h-9 rounded-full border border-zinc-200 bg-white hover:bg-[#E25C23] hover:border-[#E25C23] hover:text-white text-zinc-700 flex items-center justify-center transition-all duration-200 shadow-sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Slider or Static Grid */}
            {verifiedReviews.length > 2 ? (
              <div className="testimonial-dots [&_.slick-dots_li_button:before]:!text-[#E25C23] [&_.slick-dots_li_button:before]:!opacity-35 [&_.slick-dots_li.slick-active_button:before]:!opacity-100 [&_.slick-dots_li.slick-active_button:before]:!text-[#E25C23] [&_.slick-dots_li_button:before]:!text-[8px] [&_.slick-dots_li.slick-active_button:before]:!text-[11px] pb-6">
                <Slider {...sliderSettings} ref={sliderRef}>
                  {verifiedReviews.map((review, index) => (
                    <div key={index} className="px-2 py-2">
                      <ReviewCard review={review} />
                    </div>
                  ))}
                </Slider>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {verifiedReviews.map((review, index) => (
                  <ReviewCard key={index} review={review} />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Empty state */
          <div className="bg-gradient-to-b from-zinc-50 to-white border border-zinc-200/90 rounded-2xl p-8 sm:p-12 text-center flex flex-col items-center justify-center space-y-4">
            <div className="max-w-md">
              <h3 className="text-xl font-bold text-zinc-900">
                Be the First to Review
              </h3>
              <p className="text-zinc-600 text-sm mt-1.5 leading-relaxed">
                Have you climbed or trekked with us on {expeditionName}? Share
                your summit story, feedback on our Sherpa team, and inspire
                fellow adventurers!
              </p>
            </div>
            <button
              onClick={scrollToForm}
              type="button"
              className="inline-flex items-center px-6 py-2.5 bg-[#E25C23] hover:bg-[#c94b17] text-white font-semibold text-sm rounded-xl shadow-sm hover:shadow transition-all duration-200 cursor-pointer"
            >
              Share Your Story
            </button>
          </div>
        )}
      </div>

      {/* 3. Review Submission Form Card */}
      <div
        ref={formRef}
        className="bg-white border border-zinc-200/90 rounded-2xl shadow-sm p-6 sm:p-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-zinc-100">
          <div>
            <h3 className="text-xl sm:text-2xl font-extrabold text-zinc-900 tracking-tight">
              Share Your Experience
            </h3>
            <p className="text-zinc-600 text-sm mt-1">
              Your feedback is verified by our team to help other climbers plan
              their expedition.
            </p>
          </div>

          <div className="text-xs text-zinc-500 bg-zinc-50 px-3 py-1.5 rounded-lg border border-zinc-200">
            Honest & Verified Feedback
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Star Rating Interactive Selector */}
          <div className="bg-zinc-50 border border-zinc-200/80 rounded-xl p-4 sm:p-5">
            <label className="block text-sm font-bold text-zinc-800 mb-2">
              How was your overall expedition?{" "}
              <span className="text-[#E25C23]">*</span>
            </label>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isActive = star <= (hoveredRating || rating);
                  return (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingClick(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                      className="p-1 text-zinc-300 hover:scale-110 active:scale-95 transition-all duration-150 focus:outline-none"
                    >
                      <Star
                        className={`w-7 h-7 sm:w-8 sm:h-8 transition-colors ${
                          isActive
                            ? "text-[#E25C23] fill-[#E25C23]"
                            : "text-zinc-300 fill-zinc-100"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>

              <div className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white border border-zinc-200 text-zinc-800 shadow-2xs">
                {RATING_LABELS[hoveredRating || rating] || "Select Rating"}
              </div>
            </div>
          </div>

          {/* Name & Email Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label
                htmlFor="review-name"
                className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2"
              >
                Full Name <span className="text-[#E25C23]">*</span>
              </label>
              <input
                type="text"
                id="review-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g. Johnathan Smith"
                className="w-full px-4 py-3 bg-zinc-50/50 hover:bg-white focus:bg-white text-zinc-900 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E25C23]/25 focus:border-[#E25C23] transition-all"
              />
            </div>

            <div>
              <label
                htmlFor="review-email"
                className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2"
              >
                Email Address <span className="text-[#E25C23]">*</span>
              </label>
              <input
                type="email"
                id="review-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="e.g. john@example.com"
                className="w-full px-4 py-3 bg-zinc-50/50 hover:bg-white focus:bg-white text-zinc-900 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E25C23]/25 focus:border-[#E25C23] transition-all"
              />
              <p className="text-[11px] text-zinc-400 mt-1">
                Your email is kept confidential and will not be displayed
                publicly.
              </p>
            </div>
          </div>

          {/* Profile Photo Upload */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2">
              Climber Photo (Optional)
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border border-zinc-200/80 rounded-xl bg-zinc-50/50">
              <div className="relative w-16 h-16 rounded-full overflow-hidden bg-white border border-zinc-200 flex items-center justify-center shrink-0 shadow-2xs text-xs font-bold text-zinc-400">
                {profilePreview ? (
                  <Image
                    src={profilePreview}
                    alt="Preview"
                    fill
                    className="object-cover"
                    unoptimized
                  />
                ) : (
                  <span>Photo</span>
                )}
              </div>

              <div className="flex-1 space-y-1">
                {profilePreview ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-emerald-600">
                      Photo uploaded
                    </span>
                    <button
                      type="button"
                      onClick={removeProfileImage}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold underline underline-offset-2"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-zinc-100 text-zinc-700 text-xs font-semibold border border-zinc-300 rounded-lg cursor-pointer transition-all shadow-2xs">
                      {isUploading && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E25C23]" />
                      )}
                      <span>
                        {isUploading ? "Uploading..." : "Upload Profile Photo"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleProfileUpload}
                        disabled={isUploading}
                      />
                    </label>
                    <span className="text-xs text-zinc-400">
                      JPG, PNG under 5MB
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Review Message */}
          <div>
            <label
              htmlFor="review-message"
              className="block text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2"
            >
              Your Expedition Story <span className="text-[#E25C23]">*</span>
            </label>
            <textarea
              id="review-message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={4}
              placeholder="Tell us about the summit push, our Sherpa guides, base camp hospitality, acclimatization pace, and equipment..."
              className="w-full px-4 py-3 bg-zinc-50/50 hover:bg-white focus:bg-white text-zinc-900 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E25C23]/25 focus:border-[#E25C23] transition-all leading-relaxed"
            />
            <p className="text-[11px] text-zinc-400 mt-1">
              Be descriptive! Your summit and camp insights help future
              expedition members prepare well.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">
              Reviews are verified before public listing.
            </p>

            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#E25C23] hover:bg-[#c94b17] disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-bold text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting Review...</span>
                </>
              ) : (
                <span>Submit Review</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Sub-component for individual review cards
const ReviewCard: React.FC<{ review: Review }> = ({ review }) => {
  const [imgError, setImgError] = useState(false);
  const avatarImage =
    !imgError && review?.images && review.images.length > 0
      ? review.images[0]
      : null;

  const firstLetter = review?.name ? review.name.charAt(0).toUpperCase() : "E";

  return (
    <div className="bg-white border border-zinc-200/90 rounded-2xl p-6 sm:p-7 shadow-xs hover:shadow-md transition-all duration-300 flex flex-col justify-between h-full min-h-[260px] relative overflow-hidden group">
      {/* Top: Climber profile & Star rating */}
      <div className="relative z-10">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-[#E25C23] to-amber-600 text-white flex items-center justify-center font-extrabold text-base shadow-xs shrink-0">
              {avatarImage ? (
                <Image
                  src={avatarImage}
                  alt={`${review.name}'s profile`}
                  fill
                  className="object-cover"
                  onError={() => setImgError(true)}
                  unoptimized
                />
              ) : (
                <span>{firstLetter}</span>
              )}
            </div>

            <div>
              <h4 className="font-bold text-zinc-900 text-base leading-tight">
                {review.name}
              </h4>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="inline-flex items-center text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/80">
                  Verified Climber
                </span>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-0.5 text-[#E25C23]">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${
                    star <= review.rating
                      ? "fill-[#E25C23] text-[#E25C23]"
                      : "text-zinc-200 fill-zinc-100"
                  }`}
                />
              ))}
            </div>
            <span className="text-[11px] font-bold text-zinc-400 mt-1">
              {review.rating}.0 / 5.0
            </span>
          </div>
        </div>

        {/* Message */}
        <p className="text-zinc-700 text-sm sm:text-base leading-relaxed italic mt-3 line-clamp-6">
          &ldquo;{review.message || "Exceptional Himalayan expedition experience!"}&rdquo;
        </p>
      </div>

      {/* Bottom photo gallery if user attached extra images */}
      {review.images && review.images.length > 1 && (
        <div className="flex items-center gap-2 pt-4 border-t border-zinc-100 mt-4">
          {review.images.slice(1, 4).map((img, i) => (
            <div
              key={i}
              className="relative w-12 h-12 rounded-lg overflow-hidden border border-zinc-200"
            >
              <Image
                src={img}
                alt="Climbing photo"
                fill
                className="object-cover"
                unoptimized
              />
            </div>
          ))}
          {review.images.length > 4 && (
            <div className="w-12 h-12 rounded-lg bg-zinc-100 border border-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-500">
              +{review.images.length - 4}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PackageReview;
